import { useState } from "react";
import {
  disconnect,
  errorMessage,
  refreshTools,
  renameConnection,
  setEnabledTools,
  shareConnection,
} from "../api";
import {
  methodOf,
  scopeOf,
  slugOf,
  toolsForConnection,
  type CatalogProvider,
  type Connection,
} from "../types";
import { nextAllowList } from "../toolSelection";
import type { ToastState } from "./Toast";
import ResourcesDialog from "./ResourcesDialog";
import ToolsDialog from "./ToolsDialog";

interface Props {
  provider: CatalogProvider;
  conn: Connection;
  /** The signed-in user, to tell "your connection" from "a teammate's" on team cards. */
  uid: string;
  /** The project's team name, for the shared-with-team pill. */
  teamName: string;
  enabled: boolean;
  /** null = every tool this account can offer is exposed. */
  allowList: string[] | null;
  onToggle: (slug: string, on: boolean) => void;
  /** Opens the connect wizard aimed at this account, so a repair updates it in place. */
  onReconnect: () => void;
  /**
   * Opens the copy-to-team dialog. Null hides the action: it is only offered on the
   * caller's own connections (the copy shares *their* credential with the team), and
   * only when they have a team to copy to.
   */
  onCopy: (() => void) | null;
  /**
   * Whether "Share with team" is offered: a private connection of the caller's own, in
   * a project that belongs to a real team. A move, not a copy -- the card jumps from
   * the Private group to the Team group when it succeeds.
   */
  canShare: boolean;
  notify: (message: string, kind?: ToastState["kind"]) => void;
}

/**
 * One connected account, as its own card.
 *
 * An account is the real unit here, not the service: it has its own credential, its own
 * switch, its own tool selection and its own tool-name prefix. Two GitHub accounts are
 * two cards that happen to share an icon -- nothing about one affects the other, and the
 * card makes that plain rather than hiding it inside a shared "GitHub" panel.
 */
export default function ConnectionCard({
  provider,
  conn,
  uid,
  teamName,
  enabled,
  allowList,
  onToggle,
  onReconnect,
  onCopy,
  canShare,
  notify,
}: Props) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftSlug, setDraftSlug] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const slug = slugOf(conn);
  const method = methodOf(conn);
  const shared = scopeOf(conn) === "team";
  const mine = conn.ownerUid === uid;
  const connected = conn.status === "active";
  const broken = conn.status === "error";
  const activeMethod = provider.authMethods.find((m) => m.id === method);

  /**
   * The tools this account can actually offer. A Slack webhook exposes one tool; the
   * OAuth connection exposes four. Showing all five regardless would misrepresent what
   * the MCP server is about to hand the model.
   *
   * For a Generic MCP account these did not come from the catalog at all -- they were read
   * from the server the user pointed it at, and cached on the connection. Both cases are
   * the same question, so both go through `toolsForConnection` and this card cannot tell
   * them apart.
   */
  const availableTools = toolsForConnection(provider, conn);
  const exposed = allowList
    ? availableTools.filter((t) => allowList.includes(t.name)).length
    : availableTools.length;

  /**
   * Only a discovered tool list can go stale: the server it came from can gain and lose
   * tools whenever its owner deploys, and nothing tells us when. A provider that declares
   * its tools in MochiConnector has nothing to refresh.
   */
  const refreshable = provider.dynamicTools;

  async function handleRefresh() {
    setBusy(true);
    try {
      const res = await refreshTools({ connectionId: conn.id });
      const { tools: kept, offered } = res.data;
      notify(
        offered != null && offered > kept
          ? `${conn.accountLabel}: ${offered} tools found; ${kept} stored — enabled ` +
              `tools are kept first.`
          : `${conn.accountLabel}: ${kept} tool${kept === 1 ? "" : "s"} found.`
      );
    } catch (err) {
      // The server's own words ("could not reach", "rejected the credential") -- much more
      // use than "refresh failed", and this is the button someone presses to find out why.
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  /**
   * One write for however many tools the user just decided about -- a single checkbox or
   * everything the dialog's filter left on screen.
   *
   * `busy` is held for the round trip because the allow-list this computes from is a prop:
   * a second click landing before the listener delivers the first result would recompute
   * from the array it replaced, and undo it.
   */
  async function toggleTools(toolNames: string[], on: boolean) {
    if (toolNames.length === 0) return;

    setBusy(true);
    try {
      await setEnabledTools({
        connectionId: conn.id,
        // Shared with the Tools page, which switches the same allow-lists from the other
        // direction -- the "null means all" rule has to be the same in both or one of
        // them silently switches tools off that nobody touched.
        tools: nextAllowList(availableTools, allowList, toolNames, on),
      });
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function submitRename() {
    const next = draftSlug.trim().toLowerCase();
    if (next === slug) {
      setRenaming(false);
      return;
    }

    setBusy(true);
    setRenameError(null);
    try {
      await renameConnection({ connectionId: conn.id, slug: next });
      setRenaming(false);
      notify(`Tools for ${conn.accountLabel} are now named ${next}__*.`);
    } catch (err) {
      // The backend explains exactly what is wrong with the name; show it in place.
      setRenameError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleShare() {
    if (
      !confirm(
        `Share ${conn.accountLabel} with everyone in ${teamName}?\n\n` +
          `Every member of this project gets its tools, through your credential. ` +
          `Their servers start with it switched off; yours keeps its switch and tool ` +
          `selection exactly as they are. You can disconnect it at any time.`
      )
    ) {
      return;
    }

    setBusy(true);
    try {
      const res = await shareConnection({ connectionId: conn.id });
      notify(
        res.data.renamed
          ? `${conn.accountLabel} is now shared with ${res.data.teamName}. Its old ` +
              `name was taken in this project, so its tools are now named ` +
              `${res.data.slug}__*.`
          : `${conn.accountLabel} is now shared with ${res.data.teamName}.`
      );
    } catch (err) {
      // The backend explains what to do (reconnect first, already shared, ...).
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    if (
      !confirm(
        `Disconnect ${conn.accountLabel} from ${provider.name}?\n\n` +
          `The stored credentials are deleted and its ${slug}__* tools stop being ` +
          (shared
            ? `exposed for everyone in ${teamName} — this connection is shared. `
            : `exposed through your MCP server. `) +
          `Other ${provider.name} accounts are not affected.`
      )
    ) {
      return;
    }

    setBusy(true);
    try {
      await disconnect({ connectionId: conn.id });
      notify(`${conn.accountLabel} disconnected.`);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className={`card ${enabled && connected ? "card-live" : ""}`}>
      <header className="card-head">
        <span className="card-icon" aria-hidden="true">
          {provider.icon}
        </span>
        <div className="card-title">
          <h3>{conn.accountLabel}</h3>
          <p>{provider.name}</p>
        </div>

        {/* On a team card this is the *team's* switch: flipping it exposes or hides the
            account for every member at once. Only personal accounts are per-user. */}
        <label
          className="switch"
          title={
            !connected
              ? "This account needs reconnecting first"
              : shared
                ? `Switches this account on or off for everyone in ${teamName}`
                : ""
          }
        >
          <input
            type="checkbox"
            checked={enabled && connected}
            disabled={!connected || busy}
            onChange={(e) => onToggle(slug, e.target.checked)}
          />
          <span className="switch-track" />
        </label>
      </header>

      {/* The tool-name prefix doubles as the rename affordance: it is the thing being
          renamed, so it is also the thing you click. */}
      <div className="card-slug">
        {renaming ? (
          <form
            className="rename"
            onSubmit={(e) => {
              e.preventDefault();
              void submitRename();
            }}
          >
            <input
              autoFocus
              value={draftSlug}
              spellCheck={false}
              autoComplete="off"
              disabled={busy}
              onChange={(e) => setDraftSlug(e.target.value)}
              aria-label="Tool name prefix"
            />
            <span className="rename-suffix">__*</span>
            <button className="btn btn-sm" type="submit" disabled={busy}>
              Save
            </button>
            <button
              className="btn btn-subtle btn-sm"
              type="button"
              disabled={busy}
              onClick={() => {
                setRenaming(false);
                setRenameError(null);
              }}
            >
              Cancel
            </button>
          </form>
        ) : (
          <button
            className="slug-btn"
            title="Rename — changes what this account's tools are called"
            disabled={busy}
            onClick={() => {
              setDraftSlug(slug);
              setRenameError(null);
              setRenaming(true);
            }}
          >
            <code>{slug}__*</code>
          </button>
        )}
      </div>

      {renameError && <p className="manual-error">{renameError}</p>}

      <div className="card-status">
        {broken ? (
          <span className="pill pill-error" title={conn.lastError ?? undefined}>
            Reconnect needed
          </span>
        ) : connected ? (
          <span className="pill pill-ok">Connected</span>
        ) : (
          <span className="pill pill-muted">Revoked</span>
        )}

        {/* Which level this account lives at. Spelled out for team connections because
            everything on this card -- the credential, the switch, the tool selection --
            is shared: switching it off switches it off for the whole team. */}
        {shared && (
          <span
            className="pill"
            title={
              (mine
                ? `Shared with everyone in ${teamName}, through your account. `
                : `Shared with everyone in ${teamName} by a teammate. `) +
              `The switch and tool selection apply to the whole team.`
            }
          >
            Team{mine ? "" : " · teammate's account"}
          </span>
        )}

        {activeMethod && provider.authMethods.length > 1 && (
          <span className="pill">via {activeMethod.label}</span>
        )}

        {enabled && connected && (
          <span className="pill pill-live">
            {exposed} of {availableTools.length} tools exposed
          </span>
        )}

        {/* Only a discovered catalog can outgrow what one connection can store. When it
            has, say so: a tool silently missing from the list reads as "this server does
            not have it", which is false and unfixable from here. */}
        {provider.dynamicTools &&
          conn.toolsOffered != null &&
          conn.toolsOffered > availableTools.length && (
            <span
              className="pill"
              title={
                `This server offers ${conn.toolsOffered} tools — more than one ` +
                `connection can store. The ${availableTools.length} kept (alphabetical, ` +
                `enabled tools first) are usable; the rest are not exposed.`
              }
            >
              {conn.toolsOffered - availableTools.length} more on server
            </span>
          )}
      </div>

      <footer className="card-foot">
        <button
          className="btn btn-ghost btn-sm"
          onClick={handleDisconnect}
          disabled={busy}
        >
          Disconnect
        </button>
        {broken && (
          <button className="btn btn-sm" onClick={onReconnect} disabled={busy}>
            Reconnect
          </button>
        )}

        {/* Only on healthy connections, same as the copy below: a share hands the whole
            team a credential exactly as it is, so a broken one has to be repaired first
            -- the backend refuses it anyway; better not to offer it. */}
        {canShare && connected && (
          <button
            className="btn btn-subtle btn-sm"
            onClick={handleShare}
            disabled={busy}
            title={`Move this account to team level — everyone in ${teamName} gets its tools`}
          >
            Share with team
          </button>
        )}

        {/* Only on healthy connections: a copy carries the credential as it is, so a
            broken one would arrive already needing reconnecting -- for a whole team. */}
        {onCopy && connected && (
          <button
            className="btn btn-subtle btn-sm"
            onClick={onCopy}
            disabled={busy}
            title={
              shared
                ? "Share a copy of this account with another team"
                : "Share a copy of this account with a team"
            }
          >
            Copy to team…
          </button>
        )}

        {/* Doubles as the repair for a server that broke and came back: a refresh that
            succeeds has proved the endpoint is reachable and the token still good, which is
            the exact condition that marked it broken. */}
        {refreshable && (
          <button
            className="btn btn-subtle btn-sm"
            onClick={handleRefresh}
            disabled={busy}
            title="Ask this MCP server what tools it has now"
          >
            {busy ? "Refreshing…" : "Refresh tools"}
          </button>
        )}

        {/* Which of the account's resources (GitHub: repositories) this connection may
            touch. Only for providers that declare the capability, and only on healthy
            connections -- the picker has to ask the provider what the credential can
            reach, which a dead credential cannot answer. */}
        {provider.resources && connected && (
          <button
            className="btn btn-subtle btn-sm"
            onClick={() => setResourcesOpen(true)}
            disabled={busy}
            aria-haspopup="dialog"
            title={`Choose which ${provider.resources.noun} this connection may touch`}
          >
            {conn.allowedResources
              ? `${conn.allowedResources.length} ${provider.resources.noun}`
              : `All ${provider.resources.noun}`}
          </button>
        )}

        {/* The tool list is a decision about this one account, so it opens over the page
            rather than inside the card: expanding in place shoved every other card down
            the grid, and a Generic MCP server with thirty tools made that a scroll. */}
        <button
          className="btn btn-subtle btn-sm card-expand"
          onClick={() => setToolsOpen(true)}
          aria-haspopup="dialog"
        >
          {availableTools.length} tools
        </button>
      </footer>

      {resourcesOpen && (
        <ResourcesDialog
          provider={provider}
          conn={conn}
          teamName={shared ? teamName : null}
          notify={notify}
          onClose={() => setResourcesOpen(false)}
        />
      )}

      {toolsOpen && (
        <ToolsDialog
          provider={provider}
          conn={conn}
          slug={slug}
          tools={availableTools}
          allowList={allowList}
          enabled={enabled}
          connected={connected}
          teamName={shared ? teamName : null}
          busy={busy}
          onToggleTools={(names, on) => void toggleTools(names, on)}
          onClose={() => setToolsOpen(false)}
        />
      )}
    </article>
  );
}
